import { useEffect, useState } from "react";

export default function useTableHeight(
  containerRef: React.RefObject<HTMLDivElement>,
  showHeader: boolean
) {
  const [tableHeight, setTableHeight] = useState<number>(400); // Default fallback height

  // ResizeObserver to dynamically calculate table height
  useEffect(() => {
    const calculateTableHeight = () => {
      if (!containerRef.current) return;

      const containerHeight = containerRef.current.clientHeight;
      let headerHeight = 0;

      // Find the actual table header element
      const tableElement =
        containerRef.current.querySelector(".ant-table-thead");
      if (tableElement && showHeader) {
        headerHeight = tableElement.getBoundingClientRect().height;
      }

      // Calculate available height with padding
      const availableHeight = containerHeight - headerHeight;

      // Ensure minimum height and that we have a valid height
      const finalHeight = Math.max(availableHeight, 200);

      setTableHeight(finalHeight);
    };

    // Initial calculation with delay to ensure DOM is ready
    const initialTimer = setTimeout(calculateTableHeight, 100);

    // ResizeObserver for dynamic updates
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(calculateTableHeight);
    });

    // Window resize listener as fallback for environments where ResizeObserver might not work properly
    const handleWindowResize = () => {
      requestAnimationFrame(calculateTableHeight);
    };

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      window.addEventListener("resize", handleWindowResize);
    }

    return () => {
      clearTimeout(initialTimer);
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [showHeader, containerRef]);

  return tableHeight;
}
